import {differenceInCalendarDays} from 'date-fns';
import {DataStatus} from 'platform/components';
import {Center, Show, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import type {TenantResponseBody} from '@dms/api/shared';
import {useGetArticleStockItemOverviewQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {Section} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {parseDate} from 'shared/web';

import {StatisticsHistory} from './StatisticsHistory';

interface StatisticsProps {
  tenant: TenantResponseBody;
  handlingUnitLabel: string;
}

export function Statistics(props: StatisticsProps) {
  const {warehouseId, articleId} = useWarehouseParams();
  const formatDate = useDateTimeFormatter();
  const {data, isLoading, isFetching, isError, refetch} = useGetArticleStockItemOverviewQuery(
    {warehouseId, articleId},
    {refetchOnMountOrArgChange: true}
  );

  const oldestDate = data?.oldestPurchaseDate ? parseDate(data.oldestPurchaseDate) : undefined;
  const oldestAge = oldestDate ? differenceInCalendarDays(new Date(), oldestDate) : undefined;
  const hasOldestStock =
    (data?.totalInventory ?? 0) > 0 && oldestAge !== undefined && oldestAge >= 0;

  return (
    <Section>
      <VStack spacing={3}>
        <Show when={!data}>
          <Center width="100%">
            <DataStatus
              isLoading={isLoading || isFetching}
              isError={isError && !isFetching}
              errorMessage={i18n.t('feature.customerInsights.sectionError')}
              errorAction={{title: i18n.t('general.actions.retry'), onClick: () => void refetch()}}
            />
          </Center>
        </Show>
        <StatisticsHistory
          currency={props.tenant.currency}
          handlingUnitLabel={props.handlingUnitLabel}
          currentInventoryQuantity={data?.totalInventory}
          currentInventoryValue={data?.totalValue}
          oldestStockAge={hasOldestStock ? oldestAge : undefined}
          oldestStockDate={
            hasOldestStock && oldestDate ? formatDate('dateShort', oldestDate) : undefined
          }
        />
      </VStack>
    </Section>
  );
}
